import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'
import { reactPlugin } from '@datadog/browser-rum-react'
import config from 'constants/config'
import { reportClientError } from './errorReportingService'

let initialized = false
let logsInitialized = false
let rumInitialized = false

type DatadogBrowserConfig = {
  clientToken: string
  site: string
  service: string
  env: string
  version: string
}

function initDatadogLogs(config: DatadogBrowserConfig) {
  datadogLogs.init({
    ...config,
    forwardErrorsToLogs: false,
    sessionSampleRate: 100,
  })
  logsInitialized = true
}

function initDatadogRum(applicationId: string | undefined, config: DatadogBrowserConfig) {
  if (!applicationId) return

  datadogRum.init({
    applicationId,
    ...config,
    // Cost guard: keep RUM ingestion to 1% if it is enabled in the future.
    sessionSampleRate: 1,
    sessionReplaySampleRate: 0,
    defaultPrivacyLevel: 'mask-user-input',
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    allowedTracingUrls: [],
    // Router view tracking requires Datadog's React Router v6 wrapper; this app uses a history router.
    plugins: [reactPlugin()],
  })
  rumInitialized = true
}

export function initUserMonitoring() {
  if (initialized) return
  initialized = true

  const applicationId = config.DATADOG_RUM_APPLICATION_ID
  const clientToken = config.DATADOG_CLIENT_TOKEN
  const rumEnabled = config.DATADOG_RUM_ENABLED

  if (!clientToken) {
    return
  }

  const site = config.DATADOG_RUM_SITE || 'ap2.datadoghq.com'
  const service = 'www-le-customer'
  const env = config.APP_ENV || 'unknown'
  const commitSha = config.COMMIT_SHA || undefined
  const version = commitSha ? commitSha.substring(0, 7) : 'unknown'
  const datadogConfig = { clientToken, site, service, env, version }

  initDatadogLogs(datadogConfig)

  if (rumEnabled) initDatadogRum(applicationId, datadogConfig)
}

export function setMonitoringUser(userId: string | number | undefined | null) {
  if (typeof window === 'undefined') return
  try {
    if (userId != null && userId !== '') {
      const user = { id: String(userId) }
      if (rumInitialized) datadogRum.setUser(user)
      if (logsInitialized) datadogLogs.setUser(user)
    } else {
      if (rumInitialized) datadogRum.clearUser()
      if (logsInitialized) datadogLogs.clearUser()
    }
  } catch {
    // Silently fail
  }
}

export function reportLog(message: string, data: any, level: 'info' | 'warn' | 'error' = 'error') {
  if (typeof window === 'undefined') return
  try {
    const context = data != null && typeof data === 'object' ? data : { value: data }

    if (rumInitialized && level === 'error') {
      datadogRum.addError(new Error(message), context)
    } else if (rumInitialized) {
      datadogRum.addAction(message, { ...context, level })
    }

    if (logsInitialized) {
      datadogLogs.logger[level](message, context)
    }
  } catch {
    try {
      reportClientError(new Error('reportLog: client logging failed'), {
        originalMessage: message,
        attemptedLevel: level,
      })
    } catch {
      // Both monitoring and Sentry failed; don't crash the app
    }
  }
}

export function createLoggerWithContext(baseContext: Record<string, any>) {
  return (message: string, level: 'info' | 'warn' | 'error', additionalContext: Record<string, any> = {}) => {
    reportLog(message, { ...baseContext, ...additionalContext }, level)
  }
}
